import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import './AgeVerificationScreen.css';

const CODE_LANGS = { curl: 'cURL', node: 'Node.js', php: 'PHP', js: 'JavaScript', react: 'React' };

/** Flow types: age-estimation (camera), id-verification (document+face), digital-id (wallet), passkey (Veritage Key) */
const FLOW_TYPES = 'age-estimation | id-verification | digital-id | passkey';

const SNIPPETS = {
  redirect: {
    initiate: {
      curl: `curl -X POST "https://api.veritage.io/api/v1/age-gate/initiate?api_key=YOUR_PUBLIC_KEY&redirect_url=https://yoursite.com/callback" \\
  -H "Content-Type: application/json"`,
      node: `// GET /verify-age — redirect user to Veritage
const redirectUrl = \`\${req.protocol}://\${req.get('host')}/api/age-verified\`;
const veritageRes = await fetch(
  \`https://api.veritage.io/api/v1/age-gate/initiate?api_key=\${PUBLIC_KEY}&redirect_url=\${encodeURIComponent(redirectUrl)}\`,
  { method: 'POST', headers: { 'Content-Type': 'application/json' } }
);
const data = await veritageRes.json();
if (!veritageRes.ok) return res.status(502).send(await veritageRes.text());
let verificationUrl = data.verificationUrl;
if (verificationUrl) {
  const flow = req.query.flow || 'age-estimation'; // flow: age-estimation | id-verification | digital-id | passkey
  verificationUrl += (verificationUrl.includes('?') ? '&' : '?') + 'flow=' + encodeURIComponent(flow);
}
return res.redirect(verificationUrl);`,
      php: `$url = "https://api.veritage.io/api/v1/age-gate/initiate?" . http_build_query([
  'api_key' => PUBLIC_KEY, 'redirect_url' => 'https://yoursite.com/callback'
]);
$data = json_decode(file_get_contents($url, false, stream_context_create(['http' => ['method' => 'POST']])), true);
$verificationUrl = $data['verificationUrl'];
if ($verificationUrl) {
  $flow = $_GET['flow'] ?? 'age-estimation'; // age-estimation | id-verification | digital-id | passkey
  $verificationUrl .= (strpos($verificationUrl, '?') !== false ? '&' : '?') . 'flow=' . urlencode($flow);
}
header('Location: ' . $verificationUrl);
exit;`,
    },
    verify: {
      curl: `curl -X POST "https://api.veritage.io/api/v1/age-gate/verify-token" \\
  -H "Content-Type: application/json" \\
  -d '{"apiKey":"YOUR_SECRET_KEY","verificationToken":"TOKEN_FROM_QUERY"}'`,
      node: `// GET /api/age-verified?token=...
const token = req.query.token;
const r = await fetch('https://api.veritage.io/api/v1/age-gate/verify-token', {
  method: 'POST', headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ apiKey: SECRET_KEY, verificationToken: token })
});
const data = await r.json();
if (data.verified && data.over18) {
  res.cookie('age_verified', 'true', { httpOnly: true, signed: true, secure: true, maxAge: 86400000 });
  return res.redirect('/?verified=true');
}
res.redirect('/?verified=false');`,
      php: `$token = $_GET['token'] ?? '';
$r = file_get_contents('https://api.veritage.io/api/v1/age-gate/verify-token', false, stream_context_create([
  'http' => ['method' => 'POST', 'header' => 'Content-Type: application/json', 'content' => json_encode(['apiKey' => SECRET_KEY, 'verificationToken' => $token])]
]));
$data = json_decode($r, true);
if ($data['verified'] && $data['over18']) { $_SESSION['age_verified'] = true; header('Location: /'); exit; }
header('Location: /?verified=false'); exit;`,
    },
  },
  iframe: {
    initiate: {
      node: `// GET /api/initiate-iframe
const redirectUrl = \`\${req.protocol}://\${req.get('host')}/verified\`;
const veritageRes = await fetch(\`https://api.veritage.io/api/v1/age-gate/initiate?api_key=\${PUBLIC_KEY}&redirect_url=\${encodeURIComponent(redirectUrl)}\`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
const data = await veritageRes.json();
if (!veritageRes.ok) return res.status(502).json({ error: await veritageRes.text() });
let verificationUrl = data.verificationUrl;
if (verificationUrl) {
  const flow = req.query.flow || 'age-estimation'; // flow: age-estimation | id-verification | digital-id | passkey
  verificationUrl += (verificationUrl.includes('?') ? '&' : '?') + 'flow=' + encodeURIComponent(flow);
}
return res.json({ verificationUrl });`,
      curl: `curl -X POST "https://api.veritage.io/api/v1/age-gate/initiate?api_key=PK&redirect_url=https://yoursite.com/verified" -H "Content-Type: application/json"
# Response: { "verificationUrl": "...", "sessionToken": "..." }
# IMPORTANT: Append ?flow=... to verificationUrl before embedding (required). Flow types: age-estimation | id-verification | digital-id | passkey`,
    },
    embed: {
      js: `const res = await fetch('/api/initiate-iframe');
const { verificationUrl } = await res.json();
const iframe = document.createElement('iframe');
iframe.src = verificationUrl;
iframe.setAttribute('allow', 'camera; microphone; geolocation; publickey-credentials-get; publickey-credentials-create; digital-credentials-get');
iframe.setAttribute('sandbox', 'allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals allow-top-navigation-by-user-activation');
document.getElementById('container').appendChild(iframe);
window.addEventListener('message', (e) => {
  if (e.origin !== 'https://verify.veritage.io') return;
  if (e.data?.type === 'veritage-verification-complete')
    fetch('/api/verify', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: e.data.verificationToken }) });
});`,
      react: `const [url, setUrl] = useState(null);
useEffect(() => {
  const handler = (e) => {
    if (e.origin !== 'https://verify.veritage.io' || e.data?.type !== 'veritage-verification-complete') return;
    fetch('/api/verify', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: e.data.verificationToken }) }).then(() => setUrl(null));
  };
  window.addEventListener('message', handler); return () => window.removeEventListener('message', handler);
}, []);
const open = async () => { const r = await fetch('/api/initiate-iframe'); const d = await r.json(); setUrl(d.verificationUrl); };
return url ? <iframe src={url} allow="camera; microphone; geolocation; publickey-credentials-get; publickey-credentials-create; digital-credentials-get" sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals allow-top-navigation-by-user-activation" title="Veritage" /> : <button onClick={open}>Verify age</button>;`,
    },
    verify: `// POST /api/verify — called by frontend after postMessage
app.post('/api/verify', async (req, res) => {
  const { token } = req.body;
  if (!token) return res.status(400).json({ verified: false, error: 'Token required' });
  const r = await fetch('https://api.veritage.io/api/v1/age-gate/verify-token', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ apiKey: SECRET_KEY, verificationToken: token })
  });
  const data = await r.json();
  if (data.verified && data.over18) {
    res.cookie('age_verified', 'true', { httpOnly: true, signed: true, secure: true, maxAge: 86400000 });
    return res.json({ verified: true, over18: true });
  }
  res.json({ verified: false, over18: false });
});`,
  },
};

export default function AgeVerificationScreen() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [newSecretKey, setNewSecretKey] = useState(null);
  const [allowedRedirectUrls, setAllowedRedirectUrls] = useState('');
  const [flowTab, setFlowTab] = useState('redirect');
  const [initiateLang, setInitiateLang] = useState('node');
  const [verifyLang, setVerifyLang] = useState('node');
  const [embedLang, setEmbedLang] = useState('js');
  const [llmCopied, setLlmCopied] = useState(false);
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);

  const buildLlmCopy = () => {
    const prereq = 'Prerequisites: Add your callback URL to Allowed Redirect URLs; use apiKey and verificationToken (camelCase) in verify-token body; keep Secret Key server-side only. Append ?flow=... to verificationUrl (required — otherwise verify app shows "Invalid verification link"). Flow types: age-estimation | id-verification | digital-id | passkey.';
    const security = 'Security: Secret key server-side only. Verify tokens on backend. Use signed cookies. Iframe: validate postMessage origin.';
    const codeFence = (lang, code) => `\`\`\`${lang}\n${code}\n\`\`\`\n`;
    let out = `# Veritage Age Verification — ${flowTab === 'redirect' ? 'Redirect' : 'Iframe'} flow\n\n`;
    out += `## Prerequisites\n${prereq}\n\n`;
    if (flowTab === 'redirect') {
      out += `## Flow\n1. Backend calls initiate then redirects user to verificationUrl. Append ?flow=... (flow types: ${FLOW_TYPES}).\n2. User completes verification; Veritage redirects to your URL with ?token=...\n3. Backend reads token, calls verify-token with Secret Key, then sets session/cookie if verified && over18.\n\n`;
      out += `## 1. Initiate (backend)\nLanguage: ${CODE_LANGS[initiateLang]}\n\n`;
      out += codeFence(initiateLang === 'node' ? 'javascript' : initiateLang === 'php' ? 'php' : 'bash', SNIPPETS.redirect.initiate[initiateLang]);
      out += `## 2. Verify token (callback handler)\nLanguage: ${CODE_LANGS[verifyLang]}\n\n`;
      out += codeFence(verifyLang === 'node' ? 'javascript' : verifyLang === 'php' ? 'php' : 'bash', SNIPPETS.redirect.verify[verifyLang]);
    } else {
      out += `## Flow\n1. Backend returns verificationUrl (same initiate API, return JSON instead of redirect). Append ?flow=... to verificationUrl (required — otherwise verify app shows "Invalid verification link"). Flow types: ${FLOW_TYPES}.\n2. Frontend embeds verificationUrl in an iframe with allow="camera; microphone; geolocation; publickey-credentials-get; publickey-credentials-create; digital-credentials-get" and listens for postMessage.\n3. On veritage-verification-complete, send verificationToken to your backend; backend calls verify-token (same as Redirect step 2).\n\n`;
      const iframeInitLang = initiateLang === 'php' ? 'node' : initiateLang;
      out += `## 1. Initiate (backend — return JSON)\nLanguage: ${CODE_LANGS[iframeInitLang]}\n\n`;
      out += codeFence(iframeInitLang === 'node' ? 'javascript' : 'bash', SNIPPETS.iframe.initiate[iframeInitLang]);
      out += `## 2. Embed iframe + postMessage\nLanguage: ${CODE_LANGS[embedLang]}\n\n`;
      out += codeFence(embedLang === 'react' ? 'jsx' : 'javascript', SNIPPETS.iframe.embed[embedLang]);
      out += `## 3. Verify token (backend)\nPOST /api/verify receives { token } from frontend; call verify-token with apiKey (secret) and verificationToken.\n\n`;
      out += codeFence('javascript', SNIPPETS.iframe.verify);
      out += 'Note: Validate event.origin (e.g. https://verify.veritage.io). Always verify the token on your backend via verify-token.\n\n';
    }
    out += `## Security\n${security}\n`;
    return out;
  };

  const handleCopyForLlm = async () => {
    try {
      await navigator.clipboard.writeText(buildLlmCopy());
      setLlmCopied(true);
      setTimeout(() => setLlmCopied(false), 2500);
    } catch (e) {
      setSuccess(null);
      setError('Failed to copy to clipboard');
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    if (!showGenerateConfirm) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setShowGenerateConfirm(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showGenerateConfirm]);

  const fetchConfig = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.getAgeGateConfig();
      setConfig(data);
      
      // Set redirect URLs from config
      if (data.allowedRedirectUrls) {
        setAllowedRedirectUrls(data.allowedRedirectUrls.join('\n'));
      }
    } catch (err) {
      console.error('Failed to fetch age gate config:', err);
      setError(err.message || 'Failed to load configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateKeys = async () => {
    setShowGenerateConfirm(true);
  };

  const handleGenerateKeysConfirm = async () => {
    setShowGenerateConfirm(false);
    try {
      setIsGenerating(true);
      setError(null);
      setSuccess(null);
      const data = await api.generateApiKeys();
      
      // Show the secret key (only shown once)
      setNewSecretKey(data.secretApiKey);
      setShowSecretKey(true);
      setConfig(data);
      
      // Update redirect URLs from response
      if (data.allowedRedirectUrls) {
        setAllowedRedirectUrls(data.allowedRedirectUrls.join('\n'));
      }

      setSuccess('API keys generated successfully! Make sure to copy your secret key - it won\'t be shown again.');
    } catch (err) {
      console.error('Failed to generate API keys:', err);
      setError(err.message || 'Failed to generate API keys');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateKeysCancel = () => {
    setShowGenerateConfirm(false);
  };

  const handleSaveRedirectUrls = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      // Parse redirect URLs (one per line)
      const redirectUrlsArray = allowedRedirectUrls
        .split('\n')
        .map(url => url.trim())
        .filter(url => url.length > 0);

      const configData = {
        allowedRedirectUrls: redirectUrlsArray,
      };

      const data = await api.updateAgeGateConfig(configData);
      setConfig(data);
      if (data.allowedRedirectUrls) {
        setAllowedRedirectUrls(data.allowedRedirectUrls.join('\n'));
      }
      setSuccess('Redirect URLs saved successfully!');
    } catch (err) {
      console.error('Failed to save redirect URLs:', err);
      setError(err.message || 'Failed to save redirect URLs');
    } finally {
      setIsSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setSuccess('Copied to clipboard!');
      setTimeout(() => setSuccess(null), 2000);
    });
  };

  const maskApiKey = (key) => {
    if (!key) return 'Not set';
    return key.substring(0, 12) + '...' + key.substring(key.length - 4);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="vt-ageVerification">
        <div className="vt-pageHead">
          <h1 className="vt-pageTitle">Age Verification</h1>
        </div>
        <div className="vt-ageVerificationLoading">Loading configuration...</div>
      </div>
    );
  }

  return (
    <>
    {showGenerateConfirm && (
      <div className="vt-confirmOverlay" onClick={handleGenerateKeysCancel}>
        <div className="vt-confirmDialog" onClick={(e) => e.stopPropagation()}>
          <h3 className="vt-confirmTitle">Generate new API keys?</h3>
          <p className="vt-confirmMessage">
            Generating new API keys will invalidate your existing keys. Any integrations using the current keys will stop working until updated.
          </p>
          <div className="vt-confirmActions">
            <button className="vt-btn vt-btn--secondary" type="button" onClick={handleGenerateKeysCancel}>
              Cancel
            </button>
            <button className="vt-btn vt-btn--primary" type="button" onClick={handleGenerateKeysConfirm}>
              Generate keys
            </button>
          </div>
        </div>
      </div>
    )}
    <div className="vt-ageVerification">
      <div className="vt-pageHead">
        <h1 className="vt-pageTitle">Age Verification</h1>
        <p className="vt-pageSub vt-muted">
          Manage your API keys for age gate integration. Users can verify via Age Estimation, ID Verification, Digital ID, or Passkey (depending on device/browser support).
        </p>
      </div>

      {error && (
        <div className="vt-alert vt-alert--danger" style={{ marginBottom: '24px' }}>
          {error}
        </div>
      )}

      {success && (
        <div className="vt-alert vt-alert--success" style={{ marginBottom: '24px' }}>
          {success}
        </div>
      )}

      {/* API Keys Section */}
      <div className="vt-card" style={{ marginBottom: '24px' }}>
        <div className="vt-cardHeader">
          <h2 className="vt-cardTitle">API Keys</h2>
        </div>
        <div className="vt-cardBody">
          <div className="vt-apiKeySection">
            <div className="vt-apiKeyRow">
              <div className="vt-apiKeyLabel">Public API Key</div>
              <div className="vt-apiKeyValue">
                {config?.publicApiKey ? (
                  <>
                    <code className="vt-apiKeyCode">{maskApiKey(config.publicApiKey)}</code>
                    <button
                      className="vt-btn vt-btn--secondary vt-btn--small"
                      onClick={() => copyToClipboard(config.publicApiKey)}
                      title="Copy full key"
                    >
                      Copy
                    </button>
                  </>
                ) : (
                  <span className="vt-muted">Not generated</span>
                )}
              </div>
            </div>

            <div className="vt-apiKeyRow">
              <div className="vt-apiKeyLabel">Secret API Key</div>
              <div className="vt-apiKeyValue">
                {showSecretKey && newSecretKey ? (
                  <>
                    <code className="vt-apiKeyCode vt-apiKeyCode--secret">{newSecretKey}</code>
                    <button
                      className="vt-btn vt-btn--secondary vt-btn--small"
                      onClick={() => copyToClipboard(newSecretKey)}
                      title="Copy secret key"
                    >
                      Copy
                    </button>
                    <div className="vt-apiKeyWarning">
                      ⚠️ Save this key now - it won't be shown again!
                    </div>
                  </>
                ) : config?.publicApiKey ? (
                  <span className="vt-muted">•••••••• (hidden for security)</span>
                ) : (
                  <span className="vt-muted">Not generated</span>
                )}
              </div>
            </div>

            <div className="vt-apiKeyRow">
              <div className="vt-apiKeyLabel">Created</div>
              <div className="vt-apiKeyValue">
                {formatDate(config?.apiKeyCreatedAt)}
              </div>
            </div>

            <div className="vt-apiKeyActions">
              {config?.hasActiveSubscription === false ? (
                <div className="vt-apiKeySubscriptionRequired" style={{ marginBottom: '12px' }}>
                  <p className="vt-muted" style={{ margin: 0 }}>
                    An active subscription is required to generate API keys.
                  </p>
                  <button
                    type="button"
                    className="vt-btn vt-btn--primary"
                    onClick={() => navigate('/billing')}
                    style={{ marginTop: '8px' }}
                  >
                    Go to Billing
                  </button>
                </div>
              ) : (
                <button
                  className="vt-btn vt-btn--primary"
                  onClick={handleGenerateKeys}
                  disabled={isGenerating}
                >
                  {isGenerating ? 'Generating...' : 'Generate New API Keys'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Redirect URLs Configuration */}
      <div className="vt-card" style={{ marginBottom: '24px' }}>
        <div className="vt-cardHeader">
          <h2 className="vt-cardTitle">Allowed Redirect URLs</h2>
        </div>
        <div className="vt-cardBody">
          <div className="vt-configSection">
            <div className="vt-configField">
              <label className="vt-label">Redirect URL Patterns</label>
              <textarea
                className="vt-textarea"
                value={allowedRedirectUrls}
                onChange={(e) => setAllowedRedirectUrls(e.target.value)}
                placeholder="https://yoursite.com/*&#10;https://*.yoursite.com/*"
                rows={4}
              />
              <p className="vt-helpText vt-muted">
                One URL pattern per line. Use your domain plus <strong>/*</strong> to allow all paths (e.g. <code>https://yoursite.com/*</code>). Supports wildcards (*). These are the only URLs where users can be redirected after verification. Required for security.
              </p>
            </div>

            <div className="vt-configActions">
              <button
                className="vt-btn vt-btn--primary"
                onClick={handleSaveRedirectUrls}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Redirect URLs'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Integration Guide — tabbed (Redirect | Iframe), code snippets with language selector */}
      <div className="vt-card" style={{ marginBottom: '24px' }}>
        <div className="vt-cardHeader" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <h2 className="vt-cardTitle" style={{ margin: 0 }}>Integration Guide</h2>
          <button type="button" className="vt-btn vt-btn--secondary" onClick={handleCopyForLlm} style={{ fontSize: '0.8125rem' }}>
            {llmCopied ? '✓ Copied for LLM' : 'Copy for LLM'}
          </button>
        </div>
        <div className="vt-cardBody">
          <div style={{ marginBottom: '20px', padding: '12px 14px', background: 'var(--vt-surface-elevated)', borderRadius: '8px', border: '1px solid var(--vt-border)', fontSize: '0.875rem' }}>
            <strong>Prerequisites:</strong> Add your callback URL to Allowed Redirect URLs above; use <code>apiKey</code> and <code>verificationToken</code> (camelCase) in verify-token body; keep Secret Key server-side only. Append <code>?flow=...</code> to verificationUrl (required). Flow types: <code>age-estimation</code> | <code>id-verification</code> | <code>digital-id</code> | <code>passkey</code>.
          </div>

          <div className="vt-intGuideTabs">
            <button type="button" className={`vt-intGuideTab ${flowTab === 'redirect' ? 'vt-intGuideTab--active' : ''}`} onClick={() => setFlowTab('redirect')}>Redirect</button>
            <button type="button" className={`vt-intGuideTab ${flowTab === 'iframe' ? 'vt-intGuideTab--active' : ''}`} onClick={() => setFlowTab('iframe')}>Iframe</button>
          </div>

          {flowTab === 'redirect' && (
            <>
              <p className="vt-intGuideFlowList" style={{ listStyle: 'none', marginLeft: 0 }}>
                1. Backend calls <strong>initiate</strong> then redirects user to <code>verificationUrl</code>. Append <code>?flow=...</code> (flow types: <code>age-estimation</code> | <code>id-verification</code> | <code>digital-id</code> | <code>passkey</code>).<br />
                2. User completes verification; Veritage redirects to your URL with <code>?token=...</code>.<br />
                3. Backend reads token, calls <strong>verify-token</strong> with Secret Key, then sets session/cookie if <code>verified &amp;&amp; over18</code>.
              </p>
              <div className="vt-intGuideStep">
                <div className="vt-codeBlockLabel">1. Initiate (backend)</div>
                <div className="vt-codeBlockWrap">
                  <div className="vt-codeTabs">
                    {['curl', 'node', 'php'].map((lang) => (
                      <button key={lang} type="button" className={`vt-codeTab ${initiateLang === lang ? 'vt-codeTab--active' : ''}`} onClick={() => setInitiateLang(lang)}>{CODE_LANGS[lang]}</button>
                    ))}
                  </div>
                  <pre className="vt-codeBlock">
                    {SNIPPETS.redirect.initiate[initiateLang]}
                  </pre>
                </div>
              </div>
              <div className="vt-intGuideStep">
                <div className="vt-codeBlockLabel">2. Verify token (callback handler)</div>
                <div className="vt-codeBlockWrap">
                  <div className="vt-codeTabs">
                    {['curl', 'node', 'php'].map((lang) => (
                      <button key={lang} type="button" className={`vt-codeTab ${verifyLang === lang ? 'vt-codeTab--active' : ''}`} onClick={() => setVerifyLang(lang)}>{CODE_LANGS[lang]}</button>
                    ))}
                  </div>
                  <pre className="vt-codeBlock">
                    {SNIPPETS.redirect.verify[verifyLang]}
                  </pre>
                </div>
              </div>
            </>
          )}

          {flowTab === 'iframe' && (
            <>
              <p className="vt-intGuideFlowList" style={{ listStyle: 'none', marginLeft: 0 }}>
                1. Backend returns <code>verificationUrl</code> (same initiate API, return JSON instead of redirect). <strong>Append <code>?flow=...</code></strong> (required — otherwise verify app shows &quot;Invalid verification link&quot;). Flow types: <code>age-estimation</code> | <code>id-verification</code> | <code>digital-id</code> | <code>passkey</code>.<br />
                2. Frontend embeds <code>verificationUrl</code> in an iframe with <code>allow</code> (camera; microphone; geolocation; publickey-credentials-get; publickey-credentials-create; digital-credentials-get) and listens for <code>postMessage</code>.<br />
                3. On <code>veritage-verification-complete</code>, send <code>verificationToken</code> to your backend; backend calls verify-token (same as Redirect step 2).
              </p>
              <div className="vt-intGuideStep">
                <div className="vt-codeBlockLabel">1. Initiate (backend — return JSON)</div>
                <div className="vt-codeBlockWrap">
                  <div className="vt-codeTabs">
                    {['node', 'curl'].map((lang) => (
                      <button key={lang} type="button" className={`vt-codeTab ${initiateLang === lang ? 'vt-codeTab--active' : ''}`} onClick={() => setInitiateLang(lang)}>{CODE_LANGS[lang]}</button>
                    ))}
                  </div>
                  <pre className="vt-codeBlock">
                    {SNIPPETS.iframe.initiate[initiateLang === 'php' ? 'node' : initiateLang]}
                  </pre>
                </div>
              </div>
              <div className="vt-intGuideStep">
                <div className="vt-codeBlockLabel">2. Embed iframe + postMessage</div>
                <div className="vt-codeBlockWrap">
                  <div className="vt-codeTabs">
                    {['js', 'react'].map((lang) => (
                      <button key={lang} type="button" className={`vt-codeTab ${embedLang === lang ? 'vt-codeTab--active' : ''}`} onClick={() => setEmbedLang(lang)}>{CODE_LANGS[lang]}</button>
                    ))}
                  </div>
                  <pre className="vt-codeBlock">
                    {SNIPPETS.iframe.embed[embedLang]}
                  </pre>
                </div>
                <p className="vt-intGuideNote">Validate <code>event.origin</code> (e.g. https://verify.veritage.io). Always verify the token on your backend via verify-token.</p>
              </div>
              <div className="vt-intGuideStep">
                <div className="vt-codeBlockLabel">3. Verify token (backend)</div>
                <p className="vt-intGuideNote" style={{ marginBottom: '8px' }}>POST /api/verify receives <code>{'{ token }'}</code> from frontend; call verify-token with apiKey (secret) and verificationToken.</p>
                <div className="vt-codeBlockWrap">
                  <pre className="vt-codeBlock">
                    {SNIPPETS.iframe.verify}
                  </pre>
                </div>
              </div>
            </>
          )}

          <div style={{ marginTop: '20px', padding: '12px 14px', background: 'var(--vt-surface-elevated)', borderRadius: '8px', fontSize: '0.8125rem', color: 'var(--vt-muted)' }}>
            <strong>Security:</strong> Secret key server-side only. Verify tokens on backend. Use signed cookies. Iframe: validate postMessage origin.
          </div>
        </div>
      </div>

      {/* Info Section */}
      <div className="vt-card">
        <div className="vt-cardHeader">
          <h2 className="vt-cardTitle">Verification Methods</h2>
        </div>
        <div className="vt-cardBody">
          <p className="vt-muted" style={{ marginBottom: '16px' }}>
            Users can verify with the following methods (availability depends on device/browser support):
          </p>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            <li style={{ padding: '8px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#10b981' }}>✓</span>
              <span>Age Estimation - Fast camera-based age check</span>
            </li>
            <li style={{ padding: '8px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#10b981' }}>✓</span>
              <span>ID Verification - Document verification with optional face match</span>
            </li>
            <li style={{ padding: '8px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#10b981' }}>✓</span>
              <span>Digital ID - Verify using a wallet (Digital Credentials API)</span>
            </li>
            <li style={{ padding: '8px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#10b981' }}>✓</span>
              <span>Passkey - Reuse previous verification securely</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
    </>
  );
}
